import { useState } from 'react';

const STATUS_PIPELINE = ['Researching', 'Bid Submitted', 'Won', 'Passed'];
const STATUS_COLORS = {
  'Researching':  { bg: '#e3f2fd', color: '#1565c0' },
  'Bid Submitted':{ bg: '#fff8e1', color: '#f57f17' },
  'Won':          { bg: '#e8f5e9', color: '#2e7d32' },
  'Passed':       { bg: '#fce4ec', color: '#c62828' },
};
const TYPE_COLORS = {
  'Tax Deed':     { bg: '#ede7f6', color: '#4527a0' },
  'Tax Lien':     { bg: '#e8eaf6', color: '#283593' },
  'Fix & Flip':   { bg: '#fbe9e7', color: '#bf360c' },
  'Multi-Family': { bg: '#e0f2f1', color: '#00695c' },
};

const fmtMoney = (num) => (num < 0 ? '-$' : '$') + Math.abs(Math.round(num)).toLocaleString();
const fmtP     = (num) => num.toFixed(1) + '%';
const n        = (v)   => parseFloat(v) || 0;

function fmtNum(num) {
  if (num === undefined || num === null || isNaN(num)) return '\u2014';
  return fmtMoney(num);
}
function fmtPctNum(num) {
  if (num === undefined || num === null || isNaN(num)) return '\u2014';
  return (num * 100).toFixed(1) + '%';
}

function calcFinancing(purchasePrice, downPct, interestRate, termMonths, loanType) {
  const price = n(purchasePrice), down = n(downPct), rate = n(interestRate), term = n(termMonths);
  const downAmt = price * (down / 100);
  const loanAmt = price - downAmt;
  const mr = rate / 100 / 12;
  let monthlyPayment = 0;
  if (loanType === 'interestOnly') monthlyPayment = loanAmt * mr;
  else if (mr > 0 && term > 0) monthlyPayment = loanAmt * (mr * Math.pow(1+mr,term)) / (Math.pow(1+mr,term)-1);
  return { downAmt, loanAmt, monthlyPayment };
}

function scoreColor(pct, min) {
  return pct >= min ? '#2d6a4f' : pct >= min * 0.75 ? '#c9a84c' : '#c0392b';
}

function StatusBadge({ status }) {
  const s = STATUS_COLORS[status] || { bg: '#f0f0f0', color: '#555' };
  return <span style={{ backgroundColor: s.bg, color: s.color, padding: '3px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold' }}>{status}</span>;
}
function TypeBadge({ type }) {
  const t = TYPE_COLORS[type] || { bg: '#f0f0f0', color: '#555' };
  return <span style={{ backgroundColor: t.bg, color: t.color, padding: '3px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold' }}>{type}</span>;
}
function SecHeader({ title }) {
  return <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '14px 0 6px', borderBottom: '2px solid #1a1a2e', marginBottom: '6px' }}>{title}</div>;
}
function PRow({ label, value, bold, color, red, positive, divider }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: divider ? '2px solid #1a1a2e' : '1px solid #f5f5f5', marginTop: divider ? '4px' : 0 }}>
      <span style={{ fontSize: '13px', color: bold ? '#1a1a2e' : '#555', fontWeight: bold ? 'bold' : 'normal' }}>{label}</span>
      <span style={{ fontSize: '13px', fontWeight: bold ? 'bold' : 'normal', color: color || (red ? '#c0392b' : positive ? '#2d6a4f' : '#333') }}>{value}</span>
    </div>
  );
}
function InpRow({ label, value, onChange, suffix, placeholder }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
      <label style={{ fontSize: '13px', color: '#555', flex: 1 }}>{label}</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
        {!suffix && <span style={{ fontSize: '13px', color: '#888' }}>$</span>}
        <input type="text" inputMode="decimal" value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder || ''}
          style={{ width: '90px', padding: '4px 8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', textAlign: 'right' }} />
        {suffix && <span style={{ fontSize: '13px', color: '#888', minWidth: '22px' }}>{suffix}</span>}
      </div>
    </div>
  );
}

// ─── CSV EXPORT ───────────────────────────────────────────────────────────────
const INPUT_LABELS = {
  propAddress:'Property Address', propCity:'City', propState:'State', propZip:'ZIP',
  propCounty:'County', propParcel:'Parcel/APN', propType:'Property Type',
  propAuctionDate:'Auction Date', propListingUrl:'Zillow/Redfin URL', propNotes:'Property Notes',
  carryTaxes:'Carry Taxes (yr)', carryTaxesMo:'Carry Taxes (mo)',
  carryInsurance:'Carry Insurance (yr)', carryInsuranceMo:'Carry Insurance (mo)',
  carryHOA:'Carry HOA (yr)', carryHOAMo:'Carry HOA (mo)',
  carryUtilities:'Carry Utilities (yr)', carryUtilitiesMo:'Carry Utilities (mo)',
  carryMaintenance:'Carry Maintenance (yr)', carryMaintenanceMo:'Carry Maintenance (mo)',
  carryOther:'Carry Other (yr)', carryOtherMo:'Carry Other (mo)',
  purchasePrice:'Purchase Price', closingCostsBuy:'Closing Costs (Buy)',
  inspectionFee:'Inspection Fee', miscBuy:'Misc (Buy)',
  totalRehab:'Total Rehab', contingency:'Contingency %',
  winningBid:'Winning Bid', backTaxes:'Back Taxes', titleSearch:'Title Search',
  quietTitle:'Quiet Title', recordingFees:'Recording Fees', miscClosing:'Misc Closing',
  financingEnabled:'Financing Enabled',
  downPct:'Down Payment %', interestRate:'Interest Rate %', termMonths:'Loan Term (mo)', loanType:'Loan Type',
  arv:'ARV / Sale Price', agentCommission:'Agent Commission %', sellerClosing:'Seller Closing %',
  holdingMonths:'Holding Period (mo)', monthlyHolding:'Monthly Holding (override)', otherSaleExp:'Other Sale Expenses',
  monthlyRent:'Monthly Rent', vacancyRate:'Vacancy %', mgmtFee:'Mgmt Fee %',
  annualTax:'Annual Tax', annualInsurance:'Annual Insurance', annualMaintenance:'Annual Maintenance',
  closingCosts:'Closing Costs', initialReserves:'Initial Reserves',
  sqft:'Sq Ft', units:'Units', rentPerUnit:'Rent / Unit',
  taxes:'Taxes (yr)', insurance:'Insurance (yr)', water:'Water (yr)', trash:'Trash (yr)',
  repairs:'Repairs (yr)', capex:'CapEx (yr)', mgmtPct:'Mgmt %', threshold:'CoC Threshold %',
  lienAmount:'Lien Amount', penaltyRate:'Penalty Rate %',
  redemptionMonths:'Redemption Period (mo)', purchasePremium:'Purchase Premium',
  otherCosts:'Other Costs',
};
const INPUT_ORDER = Object.keys(INPUT_LABELS);
const COMPUTED_ORDER = [
  'Total Acquisition Cost','Rehab w/ Contingency','All-In Cost',
  'Down Payment / Cash','Loan Amount','Monthly Payment','Cash Out of Pocket','Total Cash In',
  'Effective Monthly Holding','Agent Commission $','Seller Closing $','Holding Total',
  'Total Sale Expenses','Net Sale Proceeds','Net Profit',
  'ROI on All-In (%)','CoC Sale (%)','True CoC Sale (%)',
  'Gross Annual Rent','Vacancy $','Mgmt $','Total Rental Expenses','NOI',
  'CoC Rental (%)','ROI Rental (%)',
  'Monthly Gross Rent','Annual Gross Rent','Effective Gross Income','Mgmt Fee $',
  'Total Operating Expenses','Annual Debt Service','Annual Cash Flow','Monthly Cash Flow',
  'Cap Rate (%)','Cash on Cash (%)','DSCR',
  'Total Invested','Penalty Earned','Interest Earned','Total Return','Total Received',
  'CoC Return (%)','Annualized Return (%)',
];

function computeBreakdown(deal) {
  const inp = deal.inputs || {};
  const type = deal.type;
  const out = {};
  if (type === 'Multi-Family') {
    const price = n(inp.purchasePrice);
    const f = calcFinancing(inp.purchasePrice, inp.downPct, inp.interestRate, inp.termMonths, inp.loanType);
    const totalCash = f.downAmt + n(inp.closingCosts) + n(inp.initialReserves);
    const mGross = n(inp.units)*n(inp.rentPerUnit), aGross = mGross*12;
    const egi = aGross*(1-n(inp.vacancyRate)/100);
    const mgmtFee = egi*(n(inp.mgmtPct)/100);
    const totalOpex = n(inp.taxes)+n(inp.insurance)+n(inp.water)+n(inp.trash)+n(inp.repairs)+n(inp.capex)+mgmtFee;
    const noi = egi-totalOpex, annDebt = f.monthlyPayment*12, annCF = noi-annDebt;
    out['Down Payment / Cash']=Math.round(f.downAmt); out['Loan Amount']=Math.round(f.loanAmt);
    out['Monthly Payment']=Math.round(f.monthlyPayment); out['Total Cash In']=Math.round(totalCash);
    out['Monthly Gross Rent']=Math.round(mGross); out['Annual Gross Rent']=Math.round(aGross);
    out['Effective Gross Income']=Math.round(egi); out['Mgmt Fee $']=Math.round(mgmtFee);
    out['Total Operating Expenses']=Math.round(totalOpex); out['NOI']=Math.round(noi);
    out['Annual Debt Service']=Math.round(annDebt); out['Annual Cash Flow']=Math.round(annCF);
    out['Monthly Cash Flow']=Math.round(annCF/12);
    out['Cap Rate (%)']=+(price>0?(noi/price*100).toFixed(1):0);
    out['Cash on Cash (%)']=+(totalCash>0?(annCF/totalCash*100).toFixed(1):0);
    out['DSCR']=+(annDebt>0?(noi/annDebt).toFixed(2):0);
    return out;
  }
  if (type === 'Tax Lien') {
    const lienAmt=n(inp.lienAmount), totalInv=lienAmt+n(inp.purchasePremium)+n(inp.otherCosts);
    const penEarned=lienAmt*(n(inp.penaltyRate)/100);
    const intEarned=lienAmt*(n(inp.interestRate)/100)*(n(inp.redemptionMonths)/12);
    const totRet=penEarned+intEarned, totRec=lienAmt+totRet, netP=totRec-totalInv;
    const cocR=totalInv>0?(netP/totalInv)*100:0;
    out['Total Invested']=Math.round(totalInv); out['Penalty Earned']=Math.round(penEarned);
    out['Interest Earned']=Math.round(intEarned); out['Total Return']=Math.round(totRet);
    out['Total Received']=Math.round(totRec); out['Net Profit']=Math.round(netP);
    out['CoC Return (%)']=+(cocR.toFixed(1));
    out['Annualized Return (%)']=+(n(inp.redemptionMonths)>0?(cocR/n(inp.redemptionMonths)*12).toFixed(1):0);
    return out;
  }
  let totalAcq, totalAllIn, downAmt, loanAmt, monthlyPayment, cashOutOfPocket, rehabWC=0;
  if (type === 'Tax Deed') {
    totalAcq=n(inp.winningBid)+n(inp.backTaxes)+n(inp.titleSearch)+n(inp.quietTitle)+n(inp.recordingFees)+n(inp.miscClosing);
    totalAllIn=totalAcq+n(inp.totalRehab);
    const fin=inp.financingEnabled?calcFinancing(totalAllIn,inp.downPct,inp.interestRate,inp.termMonths,inp.loanType):{downAmt:totalAllIn,loanAmt:0,monthlyPayment:0};
    downAmt=fin.downAmt; loanAmt=fin.loanAmt; monthlyPayment=fin.monthlyPayment;
    cashOutOfPocket=inp.financingEnabled?downAmt:totalAllIn;
  } else {
    totalAcq=n(inp.purchasePrice)+n(inp.closingCostsBuy)+n(inp.inspectionFee)+n(inp.miscBuy);
    rehabWC=n(inp.totalRehab)*(1+n(inp.contingency)/100);
    totalAllIn=totalAcq+rehabWC;
    const fin=calcFinancing(inp.purchasePrice,inp.downPct,inp.interestRate,inp.termMonths,inp.loanType);
    downAmt=fin.downAmt; loanAmt=fin.loanAmt; monthlyPayment=fin.monthlyPayment;
    cashOutOfPocket=downAmt+n(inp.closingCostsBuy)+n(inp.inspectionFee)+n(inp.miscBuy)+rehabWC;
  }
  const carryMo=(n(inp.carryTaxes)+n(inp.carryInsurance)+n(inp.carryHOA)+n(inp.carryUtilities)+n(inp.carryMaintenance)+n(inp.carryOther))/12;
  const effMH=n(inp.monthlyHolding)>0?n(inp.monthlyHolding):carryMo+monthlyPayment;
  const agentD=n(inp.arv)*(n(inp.agentCommission)/100);
  const closingD=n(inp.arv)*(n(inp.sellerClosing)/100);
  const holdTotal=n(inp.holdingMonths)*effMH;
  const totalSaleExp=agentD+closingD+holdTotal+n(inp.otherSaleExp);
  const netProceeds=n(inp.arv)-totalSaleExp, netProfit=netProceeds-totalAllIn;
  const roiSale=totalAllIn>0?(netProfit/totalAllIn)*100:0;
  const trueCoc=cashOutOfPocket>0?(netProfit/cashOutOfPocket)*100:0;
  const grossRent=n(inp.monthlyRent)*12, vacancy=grossRent*(n(inp.vacancyRate)/100);
  const mgmtD=grossRent*(n(inp.mgmtFee)/100);
  const effTax=n(inp.annualTax)>0?n(inp.annualTax):n(inp.carryTaxes);
  const effIns=n(inp.annualInsurance)>0?n(inp.annualInsurance):n(inp.carryInsurance);
  const effMaint=n(inp.annualMaintenance)>0?n(inp.annualMaintenance):n(inp.carryMaintenance);
  const totalRentExp=mgmtD+vacancy+effTax+effIns+effMaint+(monthlyPayment*12);
  const noiR=grossRent-totalRentExp;
  out['Total Acquisition Cost']=Math.round(totalAcq);
  if (type!=='Tax Deed') out['Rehab w/ Contingency']=Math.round(rehabWC);
  out['All-In Cost']=Math.round(totalAllIn); out['Down Payment / Cash']=Math.round(downAmt);
  out['Loan Amount']=Math.round(loanAmt); out['Monthly Payment']=Math.round(monthlyPayment);
  out['Cash Out of Pocket']=Math.round(cashOutOfPocket);
  out['Effective Monthly Holding']=Math.round(effMH);
  out['Agent Commission $']=Math.round(agentD); out['Seller Closing $']=Math.round(closingD);
  out['Holding Total']=Math.round(holdTotal); out['Total Sale Expenses']=Math.round(totalSaleExp);
  out['Net Sale Proceeds']=Math.round(netProceeds); out['Net Profit']=Math.round(netProfit);
  out['ROI on All-In (%)']=+(roiSale.toFixed(1));
  if (type==='Tax Deed') out['CoC Sale (%)']=+(roiSale.toFixed(1));
  else out['True CoC Sale (%)']=+(trueCoc.toFixed(1));
  out['Gross Annual Rent']=Math.round(grossRent); out['Vacancy $']=Math.round(vacancy);
  out['Mgmt $']=Math.round(mgmtD); out['Total Rental Expenses']=Math.round(totalRentExp);
  out['NOI']=Math.round(noiR);
  out['CoC Rental (%)']=+(cashOutOfPocket>0?(noiR/cashOutOfPocket*100).toFixed(1):0);
  out['ROI Rental (%)']=+(totalAllIn>0?(noiR/totalAllIn*100).toFixed(1):0);
  return out;
}

function csvCell(v) {
  if (v===undefined||v===null) return '';
  if (typeof v==='boolean') return v?'Yes':'No';
  const s=String(v);
  return /[",\n\r]/.test(s)?'"'+s.replace(/"/g,'""')+'"':s;
}
function buildDealsCSV(rows) {
  const seenInp=[], seenCmp=[];
  const cmpByDeal=rows.map(d=>{
    Object.keys(d.inputs||{}).forEach(k=>{ if(!seenInp.includes(k)) seenInp.push(k); });
    const c=computeBreakdown(d);
    Object.keys(c).forEach(k=>{ if(!seenCmp.includes(k)) seenCmp.push(k); });
    return c;
  });
  const inpKeys=INPUT_ORDER.filter(k=>seenInp.includes(k)).concat(seenInp.filter(k=>!INPUT_ORDER.includes(k)).sort());
  const cmpKeys=COMPUTED_ORDER.filter(k=>seenCmp.includes(k)).concat(seenCmp.filter(k=>!COMPUTED_ORDER.includes(k)).sort());
  const headers=['Deal Type','Status','Address','APN','Saved Date']
    .concat(inpKeys.map(k=>INPUT_LABELS[k]||k))
    .concat(cmpKeys)
    .concat(['IRS Lien','Title Issues','Comp 1 Address','Comp 1 Price','Comp 1 Sqft','Comp 2 Address','Comp 2 Price','Comp 2 Sqft','Comp 3 Address','Comp 3 Price','Comp 3 Sqft','Document Links','Notes']);
  const lines=[headers.map(csvCell).join(',')];
  rows.forEach((d,i)=>{
    const inp=d.inputs||{}, c=cmpByDeal[i];
    const row=[d.type||'',d.status||'',d.address||'',d.apn||'',d.savedAt?new Date(d.savedAt).toLocaleDateString():''];
    inpKeys.forEach(k=>row.push(inp[k]));
    cmpKeys.forEach(k=>row.push(c[k]!==undefined?c[k]:''));
    row.push(d.irsLien?'YES':'',d.titleIssues?'YES':'');
    row.push(d.comp1Address||'',d.comp1Price||'',d.comp1Sqft||'');
    row.push(d.comp2Address||'',d.comp2Price||'',d.comp2Sqft||'');
    row.push(d.comp3Address||'',d.comp3Price||'',d.comp3Sqft||'');
    row.push(d.docLinks||'',d.notes||'');
    lines.push(row.map(csvCell).join(','));
  });
  return lines.join('\r\n');
}
function downloadCSV(filename, csv) {
  const blob=new Blob(['\uFEFF'+csv],{type:'text/csv;charset=utf-8;'});
  const url=URL.createObjectURL(blob);
  const a=document.createElement('a'); a.href=url; a.download=filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
}

// ─── SCORECARD ────────────────────────────────────────────────────────────────
function Scorecard({ deals }) {
  const total=deals.length, byStatus={};
  STATUS_PIPELINE.forEach(s=>{ byStatus[s]=deals.filter(d=>d.status===s).length; });
  const won=deals.filter(d=>d.status==='Won');
  const totalInvested=won.reduce((s,d)=>s+((d.metrics&&d.metrics.totalAllIn)||(d.metrics&&d.metrics.totalCash)||0),0);
  return (
    <div style={{marginBottom:'20px'}}>
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'8px',marginBottom:'8px'}}>
        {[{v:total,l:'Total Deals',c:'#333'},{v:byStatus['Researching']||0,l:'Researching',c:'#1565c0'},{v:byStatus['Bid Submitted']||0,l:'Bids Out',c:'#f57f17'},{v:byStatus['Won']||0,l:'Won',c:'#2e7d32'}].map((x,i)=>(
          <div key={i} style={{backgroundColor:'white',padding:'12px',borderRadius:'8px',boxShadow:'0 1px 3px rgba(0,0,0,0.08)',textAlign:'center'}}>
            <div style={{fontSize:'22px',fontWeight:'bold',color:x.c}}>{x.v}</div>
            <div style={{fontSize:'11px',color:'#888',marginTop:'2px'}}>{x.l}</div>
          </div>
        ))}
      </div>
      {won.length>0&&(
        <div style={{backgroundColor:'#e8f5e9',border:'1px solid #a5d6a7',borderRadius:'8px',padding:'10px 14px',fontSize:'13px',color:'#2e7d32'}}>
          <strong>{won.length} deal{won.length>1?'s':''} won</strong> {'\u2014'} total capital deployed: {fmtNum(totalInvested)}
        </div>
      )}
    </div>
  );
}

function getCardStats(isMulti, m) {
  if (isMulti) return [{label:'Cash In',value:fmtNum(m.totalCash)},{label:'Mo. CF',value:fmtNum(m.monthlyCF)},{label:'CoC',value:fmtPctNum(m.coc)}];
  return [{label:'All-In',value:fmtNum(m.totalAllIn)},{label:'Net Profit',value:fmtNum(m.netProfit)},{label:'CoC Sale',value:m.cocSale?m.cocSale.toFixed(1)+'%':'\u2014'}];
}

// ─── DEAL DETAIL MODAL ────────────────────────────────────────────────────────
function DealDetail({ deal, onUpdate, onClose }) {
  const [inputs, setInputs] = useState(deal.inputs ? {...deal.inputs} : {});
  const [activeTab, setActiveTab]   = useState('acquisition');
  const [status, setStatus]         = useState(deal.status || 'Researching');
  const [apn, setApn]               = useState(deal.apn || '');
  const [irsLien, setIrsLien]       = useState(deal.irsLien || false);
  const [titleIssues, setTitleIssues] = useState(deal.titleIssues || false);
  const [docLinks, setDocLinks]     = useState(deal.docLinks || '');
  const [notes, setNotes]           = useState(deal.notes || '');
  const [c1a,setC1a]=useState(deal.comp1Address||''); const [c1p,setC1p]=useState(deal.comp1Price||''); const [c1s,setC1s]=useState(deal.comp1Sqft||'');
  const [c2a,setC2a]=useState(deal.comp2Address||''); const [c2p,setC2p]=useState(deal.comp2Price||''); const [c2s,setC2s]=useState(deal.comp2Sqft||'');
  const [c3a,setC3a]=useState(deal.comp3Address||''); const [c3p,setC3p]=useState(deal.comp3Price||''); const [c3s,setC3s]=useState(deal.comp3Sqft||'');

  const upd = (f,v) => setInputs(prev=>({...prev,[f]:v}));
  const type=deal.type, isMulti=type==='Multi-Family', isTaxLien=type==='Tax Lien', isTaxDeed=type==='Tax Deed', isFF=type==='Fix & Flip';

  // ── recompute live from inputs ──
  const ffFin  = calcFinancing(inputs.purchasePrice, inputs.downPct, inputs.interestRate, inputs.termMonths, inputs.loanType);
  const tdAllIn = n(inputs.winningBid)+n(inputs.backTaxes)+n(inputs.titleSearch)+n(inputs.quietTitle)+n(inputs.recordingFees)+n(inputs.miscClosing)+n(inputs.totalRehab);
  const tdFin  = isTaxDeed ? (inputs.financingEnabled ? calcFinancing(tdAllIn, inputs.downPct, inputs.interestRate, inputs.termMonths, inputs.loanType) : {downAmt:tdAllIn,loanAmt:0,monthlyPayment:0}) : {downAmt:0,loanAmt:0,monthlyPayment:0};
  const mFin   = calcFinancing(inputs.purchasePrice, inputs.downPct, inputs.interestRate, inputs.termMonths, inputs.loanType);

  const { downAmt, loanAmt, monthlyPayment } = isMulti ? mFin : isTaxDeed ? tdFin : ffFin;

  // Fix&Flip / TaxDeed calculations
  const totalAcqFF = n(inputs.purchasePrice)+n(inputs.closingCostsBuy)+n(inputs.inspectionFee)+n(inputs.miscBuy);
  const rehabWC    = n(inputs.totalRehab)*(1+n(inputs.contingency)/100);
  const totalAllInFF = totalAcqFF + rehabWC;
  const totalAcqTD   = n(inputs.winningBid)+n(inputs.backTaxes)+n(inputs.titleSearch)+n(inputs.quietTitle)+n(inputs.recordingFees)+n(inputs.miscClosing);
  const totalAllIn   = isTaxDeed ? tdAllIn : isFF ? totalAllInFF : 0;
  const cashOOP      = isTaxDeed ? (inputs.financingEnabled?downAmt:tdAllIn) : isFF ? (downAmt+n(inputs.closingCostsBuy)+n(inputs.inspectionFee)+n(inputs.miscBuy)+rehabWC) : 0;
  const carryMo      = (n(inputs.carryTaxes)+n(inputs.carryInsurance)+n(inputs.carryHOA)+n(inputs.carryUtilities)+n(inputs.carryMaintenance)+n(inputs.carryOther))/12;
  const autoMH       = carryMo+monthlyPayment;
  const effMH        = n(inputs.monthlyHolding)>0?n(inputs.monthlyHolding):autoMH;
  const agentD       = n(inputs.arv)*(n(inputs.agentCommission)/100);
  const closingD     = n(inputs.arv)*(n(inputs.sellerClosing)/100);
  const holdTotal    = n(inputs.holdingMonths)*effMH;
  const totalSaleExp = agentD+closingD+holdTotal+n(inputs.otherSaleExp);
  const netProceeds  = n(inputs.arv)-totalSaleExp;
  const netProfit    = netProceeds-totalAllIn;
  const roiSale      = totalAllIn>0?(netProfit/totalAllIn)*100:0;
  const trueCocSale  = cashOOP>0?(netProfit/cashOOP)*100:0;
  const grossRent    = n(inputs.monthlyRent)*12;
  const vacancy      = grossRent*(n(inputs.vacancyRate)/100);
  const mgmtDollar   = grossRent*(n(inputs.mgmtFee)/100);
  const effTax       = n(inputs.annualTax)>0?n(inputs.annualTax):n(inputs.carryTaxes);
  const effIns       = n(inputs.annualInsurance)>0?n(inputs.annualInsurance):n(inputs.carryInsurance);
  const effMaint     = n(inputs.annualMaintenance)>0?n(inputs.annualMaintenance):n(inputs.carryMaintenance);
  const totalRentExp = mgmtDollar+vacancy+effTax+effIns+effMaint+(monthlyPayment*12);
  const noiR         = grossRent-totalRentExp;
  const cocRent      = cashOOP>0?(noiR/cashOOP)*100:0;
  const roiRent      = totalAllIn>0?(noiR/totalAllIn)*100:0;

  // Multi-Family
  const mTotalCash=downAmt+n(inputs.closingCosts)+n(inputs.initialReserves);
  const mGross=n(inputs.units)*n(inputs.rentPerUnit), mAGross=mGross*12;
  const mEgi=mAGross*(1-n(inputs.vacancyRate)/100);
  const mMgmtFee=mEgi*(n(inputs.mgmtPct)/100);
  const mTotalOpex=n(inputs.taxes)+n(inputs.insurance)+n(inputs.water)+n(inputs.trash)+n(inputs.repairs)+n(inputs.capex)+mMgmtFee;
  const mNoi=mEgi-mTotalOpex, mAnnDebt=monthlyPayment*12, mAnnCF=mNoi-mAnnDebt;
  const mCapRate=n(inputs.purchasePrice)>0?mNoi/n(inputs.purchasePrice):0;
  const mCoc=mTotalCash>0?mAnnCF/mTotalCash:0;
  const mDscr=mAnnDebt>0?mNoi/mAnnDebt:0;

  // Tax Lien
  const lienAmt=n(inputs.lienAmount), lTotalInv=lienAmt+n(inputs.purchasePremium)+n(inputs.otherCosts);
  const lPen=lienAmt*(n(inputs.penaltyRate)/100);
  const lInt=lienAmt*(n(inputs.interestRate)/100)*(n(inputs.redemptionMonths)/12);
  const lTotRet=lPen+lInt, lTotRec=lienAmt+lTotRet, lNetP=lTotRec-lTotalInv;
  const lCoc=lTotalInv>0?(lNetP/lTotalInv)*100:0;
  const lAnn=n(inputs.redemptionMonths)>0?(lCoc/n(inputs.redemptionMonths))*12:0;

  const tabs = isTaxLien ? ['inputs','returns','notes'] : ['acquisition','sale','rental','scorecard','notes'];
  const tabLabels = {acquisition:'Acquisition',sale:'Sale',rental:'Rental',scorecard:'Scorecard',inputs:'Inputs',returns:'Returns',notes:'Notes & Comps'};

  function handleSave() {
    const addr = inputs.propAddress ? `${inputs.propAddress}${inputs.propCity?', '+inputs.propCity:''}${inputs.propState?', '+inputs.propState:''}` : deal.address;
    onUpdate(deal.id, { status, apn, irsLien, titleIssues, docLinks, notes, address: addr, inputs,
      comp1Address:c1a, comp1Price:c1p, comp1Sqft:c1s,
      comp2Address:c2a, comp2Price:c2p, comp2Sqft:c2s,
      comp3Address:c3a, comp3Price:c3p, comp3Sqft:c3s });
    onClose();
  }
  function handleExport() {
    const d2={...deal,inputs,status,apn,irsLien,titleIssues,docLinks,notes,comp1Address:c1a,comp1Price:c1p,comp1Sqft:c1s,comp2Address:c2a,comp2Price:c2p,comp2Sqft:c2s,comp3Address:c3a,comp3Price:c3p,comp3Sqft:c3s};
    downloadCSV('byg-deal-'+(inputs.propAddress||deal.type||'deal').replace(/[^a-z0-9]+/gi,'-').toLowerCase().slice(0,30)+'-'+new Date().toISOString().slice(0,10)+'.csv', buildDealsCSV([d2]));
  }

  const tabBtn = (tab) => ({flex:1,padding:'8px 4px',borderRadius:'6px',border:'none',backgroundColor:activeTab===tab?'#1a1a2e':'#f0f0f0',color:activeTab===tab?'white':'#333',cursor:'pointer',fontSize:'11px',fontWeight:activeTab===tab?'bold':'normal'});
  const inp90 = {width:'90px',padding:'4px 8px',border:'1px solid #ddd',borderRadius:'4px',fontSize:'14px',textAlign:'right'};

  return (
    <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,backgroundColor:'rgba(0,0,0,0.55)',zIndex:1000,overflowY:'auto',padding:'16px'}}>
      <div style={{backgroundColor:'white',borderRadius:'10px',maxWidth:'600px',margin:'0 auto',padding:'20px'}}>

        {/* Header row */}
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:'14px'}}>
          <div>
            <div style={{display:'flex',gap:'8px',alignItems:'center',marginBottom:'8px',flexWrap:'wrap'}}>
              <TypeBadge type={deal.type} />
              <select value={status} onChange={e=>setStatus(e.target.value)} style={{fontSize:'12px',padding:'4px 10px',borderRadius:'12px',border:'1px solid #ddd',cursor:'pointer',fontWeight:'bold',backgroundColor:STATUS_COLORS[status]?.bg||'#f0f0f0',color:STATUS_COLORS[status]?.color||'#555'}}>
                {STATUS_PIPELINE.map(s=><option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div style={{fontSize:'12px',color:'#888'}}>Saved {new Date(deal.savedAt).toLocaleDateString()}</div>
          </div>
          <button onClick={onClose} style={{background:'none',border:'none',fontSize:'20px',cursor:'pointer',color:'#888'}}>✕</button>
        </div>

        {/* Summary tiles */}
        {(isFF||isTaxDeed) && (
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px',marginBottom:'14px'}}>
            {[
              {label:'All-In Cost',value:fmtMoney(totalAllIn)},
              {label:'Net Profit',value:fmtMoney(netProfit),color:netProfit>=0?'#2d6a4f':'#c0392b'},
              {label:'CoC Sale',value:fmtP(isTaxDeed?roiSale:trueCocSale),bg:scoreColor(isTaxDeed?roiSale:trueCocSale,20)},
              {label:'CoC Rental',value:fmtP(cocRent),bg:scoreColor(cocRent,14)},
            ].map((x,i)=>(
              <div key={i} style={{backgroundColor:x.bg||'#f8f8f8',padding:'10px 12px',borderRadius:'8px',display:'flex',flexDirection:'column',gap:'2px'}}>
                <span style={{fontSize:'11px',color:x.bg?'rgba(255,255,255,0.8)':'#888'}}>{x.label}</span>
                <span style={{fontSize:'16px',fontWeight:'bold',color:x.bg?'white':(x.color||'#333')}}>{x.value}</span>
              </div>
            ))}
          </div>
        )}
        {isMulti && (
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px',marginBottom:'14px'}}>
            {[
              {label:'Total Cash In',value:fmtMoney(mTotalCash)},
              {label:'Monthly Cash Flow',value:fmtMoney(mAnnCF/12),color:mAnnCF>=0?'#2d6a4f':'#c0392b'},
              {label:'Cap Rate',value:fmtP(mCapRate*100),bg:scoreColor(mCapRate*100,6)},
              {label:'Cash on Cash',value:fmtP(mCoc*100),bg:scoreColor(mCoc*100,n(inputs.threshold)||14)},
            ].map((x,i)=>(
              <div key={i} style={{backgroundColor:x.bg||'#f8f8f8',padding:'10px 12px',borderRadius:'8px',display:'flex',flexDirection:'column',gap:'2px'}}>
                <span style={{fontSize:'11px',color:x.bg?'rgba(255,255,255,0.8)':'#888'}}>{x.label}</span>
                <span style={{fontSize:'16px',fontWeight:'bold',color:x.bg?'white':(x.color||'#333')}}>{x.value}</span>
              </div>
            ))}
          </div>
        )}
        {isTaxLien && (
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px',marginBottom:'14px'}}>
            {[
              {label:'Total Return',value:fmtMoney(lTotRet),color:'#2d6a4f'},
              {label:'Net Profit',value:fmtMoney(lNetP),color:lNetP>=0?'#2d6a4f':'#c0392b'},
              {label:'CoC Return',value:fmtP(lCoc),bg:scoreColor(lCoc,14)},
              {label:'Annualized',value:fmtP(lAnn),bg:scoreColor(lAnn,14)},
            ].map((x,i)=>(
              <div key={i} style={{backgroundColor:x.bg||'#f8f8f8',padding:'10px 12px',borderRadius:'8px',display:'flex',flexDirection:'column',gap:'2px'}}>
                <span style={{fontSize:'11px',color:x.bg?'rgba(255,255,255,0.8)':'#888'}}>{x.label}</span>
                <span style={{fontSize:'16px',fontWeight:'bold',color:x.bg?'white':(x.color||'#333')}}>{x.value}</span>
              </div>
            ))}
          </div>
        )}

        {/* Tab row */}
        <div style={{display:'flex',gap:'6px',marginBottom:'12px'}}>
          {tabs.map(tab=><button key={tab} onClick={()=>setActiveTab(tab)} style={tabBtn(tab)}>{tabLabels[tab]}</button>)}
        </div>

        <div style={{backgroundColor:'#fafafa',borderRadius:'8px',padding:'14px',boxShadow:'0 1px 3px rgba(0,0,0,0.06)'}}>

          {/* ── ACQUISITION TAB ── */}
          {activeTab==='acquisition' && (
            <div>
              <SecHeader title="Property Info" />
              <InpRow label="Address" value={inputs.propAddress} onChange={v=>upd('propAddress',v)} suffix=" " />
              <InpRow label="City" value={inputs.propCity} onChange={v=>upd('propCity',v)} suffix=" " />
              <InpRow label="State" value={inputs.propState} onChange={v=>upd('propState',v)} suffix=" " />
              <InpRow label="ZIP" value={inputs.propZip} onChange={v=>upd('propZip',v)} suffix=" " />
              <InpRow label="County" value={inputs.propCounty} onChange={v=>upd('propCounty',v)} suffix=" " />
              <InpRow label="APN / Parcel #" value={apn} onChange={setApn} suffix=" " />
              <InpRow label="Auction Date" value={inputs.propAuctionDate} onChange={v=>upd('propAuctionDate',v)} suffix=" " />
              <InpRow label="Zillow / Redfin URL" value={inputs.propListingUrl} onChange={v=>upd('propListingUrl',v)} suffix=" " />
              <div style={{display:'flex',gap:'20px',padding:'10px 0',borderBottom:'1px solid #f0f0f0'}}>
                <label style={{display:'flex',alignItems:'center',gap:'6px',fontSize:'13px',cursor:'pointer'}}><input type="checkbox" checked={irsLien} onChange={e=>setIrsLien(e.target.checked)} /> ! IRS Lien</label>
                <label style={{display:'flex',alignItems:'center',gap:'6px',fontSize:'13px',cursor:'pointer'}}><input type="checkbox" checked={titleIssues} onChange={e=>setTitleIssues(e.target.checked)} /> ! Title Issues</label>
              </div>

              {isFF && (<>
                <SecHeader title="Purchase Costs" />
                <InpRow label="Purchase Price" value={inputs.purchasePrice} onChange={v=>upd('purchasePrice',v)} />
                <InpRow label="Closing Costs (Buy)" value={inputs.closingCostsBuy} onChange={v=>upd('closingCostsBuy',v)} />
                <InpRow label="Inspection Fee" value={inputs.inspectionFee} onChange={v=>upd('inspectionFee',v)} />
                <InpRow label="Misc Costs" value={inputs.miscBuy} onChange={v=>upd('miscBuy',v)} />
                <PRow label="Total Acquisition" value={fmtMoney(totalAcqFF)} bold />
                <SecHeader title="Rehab / CapEx" />
                <InpRow label="Total Rehab Estimate" value={inputs.totalRehab} onChange={v=>upd('totalRehab',v)} />
                <InpRow label="Contingency Buffer" value={inputs.contingency} onChange={v=>upd('contingency',v)} suffix="%" />
                <PRow label="Rehab w/ Contingency" value={fmtMoney(rehabWC)} />
                <div style={{backgroundColor:'#1a1a2e',color:'white',padding:'12px',borderRadius:'6px',marginTop:'10px',display:'flex',justifyContent:'space-between',fontWeight:'bold'}}>
                  <span>Total All-In Cost</span><span>{fmtMoney(totalAllInFF)}</span>
                </div>
              </>)}

              {isTaxDeed && (<>
                <SecHeader title="Acquisition Costs" />
                <InpRow label="Winning Bid" value={inputs.winningBid} onChange={v=>upd('winningBid',v)} />
                <InpRow label="Back Taxes" value={inputs.backTaxes} onChange={v=>upd('backTaxes',v)} />
                <InpRow label="Title Search" value={inputs.titleSearch} onChange={v=>upd('titleSearch',v)} />
                <InpRow label="Quiet Title" value={inputs.quietTitle} onChange={v=>upd('quietTitle',v)} />
                <InpRow label="Recording Fees" value={inputs.recordingFees} onChange={v=>upd('recordingFees',v)} />
                <InpRow label="Misc Closing" value={inputs.miscClosing} onChange={v=>upd('miscClosing',v)} />
                <InpRow label="Rehab Estimate" value={inputs.totalRehab} onChange={v=>upd('totalRehab',v)} />
                <PRow label="Total Acquisition" value={fmtMoney(totalAcqTD)} bold />
                <div style={{backgroundColor:'#1a1a2e',color:'white',padding:'12px',borderRadius:'6px',marginTop:'10px',display:'flex',justifyContent:'space-between',fontWeight:'bold'}}>
                  <span>Total All-In Cost</span><span>{fmtMoney(tdAllIn)}</span>
                </div>
              </>)}

              {isMulti && (<>
                <SecHeader title="Purchase" />
                <InpRow label="Purchase Price" value={inputs.purchasePrice} onChange={v=>upd('purchasePrice',v)} />
                <InpRow label="Closing Costs" value={inputs.closingCosts} onChange={v=>upd('closingCosts',v)} />
                <InpRow label="Initial Reserves" value={inputs.initialReserves} onChange={v=>upd('initialReserves',v)} />
                <InpRow label="Sq Ft" value={inputs.sqft} onChange={v=>upd('sqft',v)} suffix=" " />
                <SecHeader title="Income" />
                <InpRow label="Units" value={inputs.units} onChange={v=>upd('units',v)} suffix=" " />
                <InpRow label="Rent / Unit / Month" value={inputs.rentPerUnit} onChange={v=>upd('rentPerUnit',v)} />
                <InpRow label="Vacancy Rate" value={inputs.vacancyRate} onChange={v=>upd('vacancyRate',v)} suffix="%" />
                <PRow label="Monthly Gross Rent" value={fmtMoney(mGross)} />
                <PRow label="Effective Gross Income (yr)" value={fmtMoney(mEgi)} bold />
                <SecHeader title="Operating Expenses (Annual)" />
                <InpRow label="Mgmt Fee" value={inputs.mgmtPct} onChange={v=>upd('mgmtPct',v)} suffix="%" />
                <InpRow label="Taxes" value={inputs.taxes} onChange={v=>upd('taxes',v)} />
                <InpRow label="Insurance" value={inputs.insurance} onChange={v=>upd('insurance',v)} />
                <InpRow label="Water" value={inputs.water} onChange={v=>upd('water',v)} />
                <InpRow label="Trash" value={inputs.trash} onChange={v=>upd('trash',v)} />
                <InpRow label="Repairs" value={inputs.repairs} onChange={v=>upd('repairs',v)} />
                <InpRow label="CapEx" value={inputs.capex} onChange={v=>upd('capex',v)} />
                <PRow label="Total Operating Expenses" value={fmtMoney(mTotalOpex)} bold />
              </>)}

              {!isTaxLien && (<>
                <SecHeader title="Financing" />
                <InpRow label="Down Payment" value={inputs.downPct} onChange={v=>upd('downPct',v)} suffix="%" />
                <InpRow label="Interest Rate" value={inputs.interestRate} onChange={v=>upd('interestRate',v)} suffix="%" />
                <InpRow label="Loan Term" value={inputs.termMonths} onChange={v=>upd('termMonths',v)} suffix="mo" />
                <PRow label="Down Payment Amount" value={fmtMoney(downAmt)} />
                <PRow label="Loan Amount" value={fmtMoney(loanAmt)} />
                <PRow label="Monthly Payment" value={fmtMoney(monthlyPayment)} bold color="#1a1a2e" />
              </>)}

              {(isFF||isTaxDeed) && (<>
                <SecHeader title="Carrying Costs (Annual)" />
                <InpRow label="Property Tax" value={inputs.carryTaxes} onChange={v=>upd('carryTaxes',v)} />
                <InpRow label="Insurance" value={inputs.carryInsurance} onChange={v=>upd('carryInsurance',v)} />
                <InpRow label="HOA" value={inputs.carryHOA} onChange={v=>upd('carryHOA',v)} />
                <InpRow label="Utilities" value={inputs.carryUtilities} onChange={v=>upd('carryUtilities',v)} />
                <InpRow label="Maintenance" value={inputs.carryMaintenance} onChange={v=>upd('carryMaintenance',v)} />
                <InpRow label="Other" value={inputs.carryOther} onChange={v=>upd('carryOther',v)} />
                <PRow label="Monthly Cost to Own" value={fmtMoney(carryMo)} bold color="#c9a84c" />
              </>)}
            </div>
          )}

          {/* ── SALE TAB ── */}
          {activeTab==='sale' && (isFF||isTaxDeed) && (
            <div>
              <SecHeader title="Sale Inputs" />
              <InpRow label="ARV / Sale Price" value={inputs.arv} onChange={v=>upd('arv',v)} />
              <InpRow label="Agent Commission" value={inputs.agentCommission} onChange={v=>upd('agentCommission',v)} suffix="%" />
              <InpRow label="Seller Closing Costs" value={inputs.sellerClosing} onChange={v=>upd('sellerClosing',v)} suffix="%" />
              <InpRow label="Holding Period" value={inputs.holdingMonths} onChange={v=>upd('holdingMonths',v)} suffix="mo" />
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'6px 0',borderBottom:'1px solid #f0f0f0'}}>
                <div style={{flex:1}}>
                  <span style={{fontSize:'13px',color:'#555'}}>Monthly Holding Cost</span>
                  {n(inputs.monthlyHolding)===0&&autoMH>0&&<span style={{fontSize:'11px',color:'#c9a84c',marginLeft:'6px',fontStyle:'italic'}}>auto</span>}
                </div>
                <div style={{display:'flex',alignItems:'center',gap:'4px'}}>
                  <span style={{fontSize:'13px',color:'#888'}}>$</span>
                  <input type="text" inputMode="decimal" value={inputs.monthlyHolding||''} onChange={e=>upd('monthlyHolding',e.target.value)} placeholder={autoMH>0?Math.round(autoMH).toString():''}
                    style={{...inp90,border:'1px solid '+(n(inputs.monthlyHolding)===0&&autoMH>0?'#c9a84c':'#ddd'),backgroundColor:n(inputs.monthlyHolding)===0&&autoMH>0?'#fffbeb':'white'}} />
                </div>
              </div>
              <InpRow label="Other Sale Expenses" value={inputs.otherSaleExp} onChange={v=>upd('otherSaleExp',v)} />
              <SecHeader title="Sale P&L Walkdown" />
              {[
                {label:'Sale Price (ARV)', value:fmtMoney(n(inputs.arv)), positive:true},
                {label:`  \u2212 Agent Commission (${n(inputs.agentCommission)}%)`, value:'('+fmtMoney(agentD)+')', red:true},
                {label:`  \u2212 Seller Closing (${n(inputs.sellerClosing)}%)`, value:'('+fmtMoney(closingD)+')', red:true},
                {label:`  \u2212 Holding (${n(inputs.holdingMonths)} mo \xd7 ${fmtMoney(effMH)})`, value:'('+fmtMoney(holdTotal)+')', red:true},
                {label:'  \u2212 Other Sale Expenses', value:'('+fmtMoney(n(inputs.otherSaleExp))+')', red:true},
                {label:'Net Sale Proceeds', value:fmtMoney(netProceeds), bold:true, divider:true},
                ...(isFF?[
                  {label:'  \u2212 Purchase Price', value:'('+fmtMoney(n(inputs.purchasePrice))+')', red:true},
                  {label:'  \u2212 Closing / Insp / Misc', value:'('+fmtMoney(n(inputs.closingCostsBuy)+n(inputs.inspectionFee)+n(inputs.miscBuy))+')', red:true},
                  {label:'  \u2212 Rehab (w/ contingency)', value:'('+fmtMoney(rehabWC)+')', red:true},
                ]:[
                  {label:'  \u2212 Winning Bid', value:'('+fmtMoney(n(inputs.winningBid))+')', red:true},
                  {label:'  \u2212 Back Taxes & Fees', value:'('+fmtMoney(n(inputs.backTaxes)+n(inputs.titleSearch)+n(inputs.quietTitle)+n(inputs.recordingFees)+n(inputs.miscClosing))+')', red:true},
                  {label:'  \u2212 Rehab', value:'('+fmtMoney(n(inputs.totalRehab))+')', red:true},
                ]),
                {label:'Net Profit', value:fmtMoney(netProfit), bold:true, divider:true, color:netProfit>=0?'#2d6a4f':'#c0392b'},
              ].map((r,i)=><PRow key={i} {...r} />)}
            </div>
          )}

          {/* ── RENTAL TAB ── */}
          {activeTab==='rental' && (isFF||isTaxDeed||isMulti) && (
            <div>
              {(isFF||isTaxDeed) && (<>
                <SecHeader title="Buy & Hold Scenario" />
                <InpRow label="Monthly Gross Rent" value={inputs.monthlyRent} onChange={v=>upd('monthlyRent',v)} />
                <InpRow label="Vacancy Rate" value={inputs.vacancyRate} onChange={v=>upd('vacancyRate',v)} suffix="%" />
                <InpRow label="Mgmt Fee" value={inputs.mgmtFee} onChange={v=>upd('mgmtFee',v)} suffix="%" />
                {[{label:'Annual Property Tax',field:'annualTax',carry:'carryTaxes'},{label:'Annual Insurance',field:'annualInsurance',carry:'carryInsurance'},{label:'Annual Maintenance',field:'annualMaintenance',carry:'carryMaintenance'}].map(({label,field,carry})=>{
                  const autoVal=n(inputs[carry]);
                  return (
                    <div key={field} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'6px 0',borderBottom:'1px solid #f0f0f0'}}>
                      <div style={{flex:1}}>
                        <span style={{fontSize:'13px',color:'#555'}}>{label}</span>
                        {!inputs[field]&&autoVal>0&&<span style={{fontSize:'11px',color:'#2d6a4f',marginLeft:'6px',fontStyle:'italic'}}>auto</span>}
                      </div>
                      <div style={{display:'flex',alignItems:'center',gap:'4px'}}>
                        {inputs[field]&&autoVal>0&&<button onClick={()=>upd(field,'')} style={{fontSize:'10px',color:'#c9a84c',background:'none',border:'1px solid #c9a84c',borderRadius:'3px',padding:'1px 5px',cursor:'pointer'}}>auto</button>}
                        <span style={{fontSize:'13px',color:'#888'}}>$</span>
                        <input type="text" inputMode="decimal" value={inputs[field]||''} placeholder={autoVal>0?Math.round(autoVal).toString():'0'} onChange={e=>upd(field,e.target.value)}
                          style={{...inp90,border:'1px solid '+(inputs[field]?'#c9a84c':'#ddd'),backgroundColor:inputs[field]?'#fffbeb':'white'}} />
                      </div>
                    </div>
                  );
                })}
                <SecHeader title="Rental P&L Walkdown (Annual)" />
                {[
                  {label:'Gross Rental Income', value:fmtMoney(grossRent), positive:true},
                  {label:`  \u2212 Vacancy (${n(inputs.vacancyRate)}%)`, value:'('+fmtMoney(vacancy)+')', red:true},
                  {label:'Effective Gross Income', value:fmtMoney(grossRent*(1-n(inputs.vacancyRate)/100)), bold:true, divider:true},
                  {label:`  \u2212 Mgmt Fee (${n(inputs.mgmtFee)}%)`, value:'('+fmtMoney(mgmtDollar)+')', red:true},
                  {label:'  \u2212 Property Tax', value:'('+fmtMoney(effTax)+')', red:true},
                  {label:'  \u2212 Insurance', value:'('+fmtMoney(effIns)+')', red:true},
                  {label:'  \u2212 Maintenance', value:'('+fmtMoney(effMaint)+')', red:true},
                  {label:'NOI', value:fmtMoney(noiR), bold:true, divider:true, color:noiR>=0?'#2d6a4f':'#c0392b'},
                  {label:'  \u2212 Annual Debt Service', value:'('+fmtMoney(monthlyPayment*12)+')', red:true},
                  {label:'Annual Cash Flow', value:fmtMoney(noiR-monthlyPayment*12), bold:true, divider:true, color:(noiR-monthlyPayment*12)>=0?'#2d6a4f':'#c0392b'},
                  {label:'Monthly Cash Flow', value:fmtMoney((noiR-monthlyPayment*12)/12), bold:true, color:(noiR-monthlyPayment*12)>=0?'#2d6a4f':'#c0392b'},
                ].map((r,i)=><PRow key={i} {...r} />)}
              </>)}
              {isMulti && (<>
                <SecHeader title="Multi-Family P&L (Annual)" />
                {[
                  {label:'Annual Gross Rent', value:fmtMoney(mAGross), positive:true},
                  {label:`  \u2212 Vacancy (${n(inputs.vacancyRate)}%)`, value:'('+fmtMoney(mAGross-mEgi)+')', red:true},
                  {label:'Effective Gross Income', value:fmtMoney(mEgi), bold:true, divider:true},
                  {label:'  \u2212 Mgmt Fee', value:'('+fmtMoney(mMgmtFee)+')', red:true},
                  {label:'  \u2212 Taxes', value:'('+fmtMoney(n(inputs.taxes))+')', red:true},
                  {label:'  \u2212 Insurance', value:'('+fmtMoney(n(inputs.insurance))+')', red:true},
                  {label:'  \u2212 Water / Trash / Repairs / CapEx', value:'('+fmtMoney(n(inputs.water)+n(inputs.trash)+n(inputs.repairs)+n(inputs.capex))+')', red:true},
                  {label:'NOI', value:fmtMoney(mNoi), bold:true, divider:true, color:mNoi>=0?'#2d6a4f':'#c0392b'},
                  {label:'  \u2212 Annual Debt Service', value:'('+fmtMoney(mAnnDebt)+')', red:true},
                  {label:'Annual Cash Flow', value:fmtMoney(mAnnCF), bold:true, divider:true, color:mAnnCF>=0?'#2d6a4f':'#c0392b'},
                  {label:'Monthly Cash Flow', value:fmtMoney(mAnnCF/12), bold:true, color:mAnnCF>=0?'#2d6a4f':'#c0392b'},
                ].map((r,i)=><PRow key={i} {...r} />)}
              </>)}
            </div>
          )}

          {/* ── SCORECARD TAB ── */}
          {activeTab==='scorecard' && (
            <div>
              {(isFF||isTaxDeed) && (<>
                <div style={{backgroundColor:'#f0f8ff',border:'1px solid #dde8f5',borderRadius:'8px',padding:'12px 14px',marginBottom:'12px'}}>
                  <div style={{fontSize:'12px',fontWeight:'bold',color:'#1a1a2e',marginBottom:'8px'}}>Cash Out of Pocket</div>
                  <div style={{display:'flex',justifyContent:'space-between',fontSize:'13px',color:'#555',padding:'3px 0'}}><span>Down Payment</span><span>{fmtMoney(downAmt)}</span></div>
                  {isFF&&<div style={{display:'flex',justifyContent:'space-between',fontSize:'13px',color:'#555',padding:'3px 0'}}><span>Closing / Insp / Misc</span><span>{fmtMoney(n(inputs.closingCostsBuy)+n(inputs.inspectionFee)+n(inputs.miscBuy))}</span></div>}
                  {isFF&&<div style={{display:'flex',justifyContent:'space-between',fontSize:'13px',color:'#555',padding:'3px 0'}}><span>Rehab (w/ contingency)</span><span>{fmtMoney(rehabWC)}</span></div>}
                  <div style={{display:'flex',justifyContent:'space-between',fontSize:'14px',fontWeight:'bold',color:'#1a1a2e',borderTop:'1px solid #dde8f5',marginTop:'6px',paddingTop:'6px'}}><span>Total Cash Out of Pocket</span><span>{fmtMoney(cashOOP)}</span></div>
                </div>
                {[{label:'Total All-In Cost',value:fmtMoney(totalAllIn)},{label:'Loan Amount',value:fmtMoney(loanAmt)},{label:'Monthly Payment',value:fmtMoney(monthlyPayment)},{label:'Net Profit',value:fmtMoney(netProfit),color:netProfit>=0?'#2d6a4f':'#c0392b'}].map((x,i)=>(
                  <div key={i} style={{backgroundColor:'#f8f8f8',padding:'10px 12px',borderRadius:'6px',marginBottom:'6px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                    <span style={{fontSize:'13px',color:'#555'}}>{x.label}</span>
                    <span style={{fontSize:'14px',fontWeight:'bold',color:x.color||'#333'}}>{x.value}</span>
                  </div>
                ))}
                <div style={{margin:'14px 0 6px',fontSize:'12px',fontWeight:'bold',color:'#888',textTransform:'uppercase',letterSpacing:'0.5px'}}>Flip Returns</div>
                {[
                  {label:'ROI on Total Investment',value:fmtP(roiSale),sub:'Net profit \xf7 all-in cost',bg:scoreColor(roiSale,20)},
                  {label:'Cash-on-Cash Return',value:fmtP(isTaxDeed?roiSale:trueCocSale),sub:'Net profit \xf7 cash out of pocket',bg:scoreColor(isTaxDeed?roiSale:trueCocSale,20)},
                ].map((x,i)=>(
                  <div key={i} style={{backgroundColor:x.bg,color:'white',padding:'12px 14px',borderRadius:'6px',marginBottom:'8px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                    <div><div style={{fontSize:'13px',fontWeight:'bold'}}>{x.label}</div><div style={{fontSize:'11px',color:'rgba(255,255,255,0.75)',marginTop:'2px'}}>{x.sub}</div></div>
                    <span style={{fontSize:'22px',fontWeight:'bold'}}>{x.value}</span>
                  </div>
                ))}
                <div style={{margin:'14px 0 6px',fontSize:'12px',fontWeight:'bold',color:'#888',textTransform:'uppercase',letterSpacing:'0.5px'}}>Rental Returns</div>
                <div style={{backgroundColor:'#f8f8f8',padding:'10px 12px',borderRadius:'6px',marginBottom:'6px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <span style={{fontSize:'13px',color:'#555'}}>Annual NOI</span>
                  <span style={{fontSize:'14px',fontWeight:'bold',color:noiR>=0?'#2d6a4f':'#c0392b'}}>{fmtMoney(noiR)}</span>
                </div>
                {[
                  {label:'ROI on Total Investment',value:fmtP(roiRent),sub:'NOI \xf7 all-in cost',bg:scoreColor(roiRent,14)},
                  {label:'Cash-on-Cash Return',value:fmtP(cocRent),sub:'NOI \xf7 cash out of pocket',bg:scoreColor(cocRent,14)},
                ].map((x,i)=>(
                  <div key={i} style={{backgroundColor:x.bg,color:'white',padding:'12px 14px',borderRadius:'6px',marginBottom:'8px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                    <div><div style={{fontSize:'13px',fontWeight:'bold'}}>{x.label}</div><div style={{fontSize:'11px',color:'rgba(255,255,255,0.75)',marginTop:'2px'}}>{x.sub}</div></div>
                    <span style={{fontSize:'22px',fontWeight:'bold'}}>{x.value}</span>
                  </div>
                ))}
              </>)}
              {isMulti && (<>
                {[{label:'Total Cash In',value:fmtMoney(mTotalCash)},{label:'Down Payment',value:fmtMoney(downAmt)},{label:'Loan Amount',value:fmtMoney(loanAmt)},{label:'Monthly Payment',value:fmtMoney(monthlyPayment)},{label:'NOI',value:fmtMoney(mNoi),color:mNoi>=0?'#2d6a4f':'#c0392b'},{label:'DSCR',value:mDscr.toFixed(2)}].map((x,i)=>(
                  <div key={i} style={{backgroundColor:'#f8f8f8',padding:'10px 12px',borderRadius:'6px',marginBottom:'6px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                    <span style={{fontSize:'13px',color:'#555'}}>{x.label}</span>
                    <span style={{fontSize:'14px',fontWeight:'bold',color:x.color||'#333'}}>{x.value}</span>
                  </div>
                ))}
                {[{label:'Cap Rate',value:fmtP(mCapRate*100),sub:'NOI \xf7 purchase price',bg:scoreColor(mCapRate*100,6)},{label:'Cash on Cash',value:fmtP(mCoc*100),sub:'Cash flow \xf7 total cash in',bg:scoreColor(mCoc*100,n(inputs.threshold)||14)}].map((x,i)=>(
                  <div key={i} style={{backgroundColor:x.bg,color:'white',padding:'12px 14px',borderRadius:'6px',marginBottom:'8px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                    <div><div style={{fontSize:'13px',fontWeight:'bold'}}>{x.label}</div><div style={{fontSize:'11px',color:'rgba(255,255,255,0.75)',marginTop:'2px'}}>{x.sub}</div></div>
                    <span style={{fontSize:'22px',fontWeight:'bold'}}>{x.value}</span>
                  </div>
                ))}
              </>)}
              {isTaxLien && (<>
                {[{label:'Total Invested',value:fmtMoney(lTotalInv)},{label:'Penalty Earned',value:fmtMoney(lPen),color:'#2d6a4f'},{label:'Interest Earned',value:fmtMoney(lInt),color:'#2d6a4f'},{label:'Total Return',value:fmtMoney(lTotRet),color:'#2d6a4f'},{label:'Net Profit',value:fmtMoney(lNetP),color:lNetP>=0?'#2d6a4f':'#c0392b'}].map((x,i)=>(
                  <div key={i} style={{backgroundColor:'#f8f8f8',padding:'10px 12px',borderRadius:'6px',marginBottom:'6px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                    <span style={{fontSize:'13px',color:'#555'}}>{x.label}</span>
                    <span style={{fontSize:'14px',fontWeight:'bold',color:x.color||'#333'}}>{x.value}</span>
                  </div>
                ))}
                {[{label:'CoC Return',value:fmtP(lCoc),sub:'Based on total invested',bg:scoreColor(lCoc,14)},{label:'Annualized Return',value:fmtP(lAnn),sub:'Prorated for redemption period',bg:scoreColor(lAnn,14)}].map((x,i)=>(
                  <div key={i} style={{backgroundColor:x.bg,color:'white',padding:'12px 14px',borderRadius:'6px',marginBottom:'8px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                    <div><div style={{fontSize:'13px',fontWeight:'bold'}}>{x.label}</div><div style={{fontSize:'11px',color:'rgba(255,255,255,0.75)',marginTop:'2px'}}>{x.sub}</div></div>
                    <span style={{fontSize:'22px',fontWeight:'bold'}}>{x.value}</span>
                  </div>
                ))}
              </>)}
            </div>
          )}

          {/* ── TAX LIEN INPUTS TAB ── */}
          {activeTab==='inputs' && isTaxLien && (
            <div>
              <SecHeader title="Property Info" />
              <InpRow label="Address" value={inputs.propAddress} onChange={v=>upd('propAddress',v)} suffix=" " />
              <InpRow label="County" value={inputs.propCounty} onChange={v=>upd('propCounty',v)} suffix=" " />
              <InpRow label="State" value={inputs.propState||inputs.state} onChange={v=>upd('state',v)} suffix=" " />
              <SecHeader title="Lien Details" />
              <InpRow label="Lien Amount" value={inputs.lienAmount} onChange={v=>upd('lienAmount',v)} />
              <InpRow label="Purchase Premium" value={inputs.purchasePremium} onChange={v=>upd('purchasePremium',v)} />
              <InpRow label="Other Costs" value={inputs.otherCosts} onChange={v=>upd('otherCosts',v)} />
              <PRow label="Total Invested" value={fmtMoney(lTotalInv)} bold color="#1a1a2e" />
              <SecHeader title="Return Parameters" />
              <InpRow label="Penalty Rate" value={inputs.penaltyRate} onChange={v=>upd('penaltyRate',v)} suffix="%" />
              <InpRow label="Interest Rate" value={inputs.interestRate} onChange={v=>upd('interestRate',v)} suffix="%" />
              <InpRow label="Redemption Period" value={inputs.redemptionMonths} onChange={v=>upd('redemptionMonths',v)} suffix="mo" />
            </div>
          )}
          {activeTab==='returns' && isTaxLien && (
            <div>
              <SecHeader title="Returns Breakdown" />
              {[
                {label:'Lien Amount', value:fmtMoney(lienAmt), positive:true},
                {label:'Penalty Earned', value:fmtMoney(lPen), positive:true},
                {label:'Interest Earned', value:fmtMoney(lInt), positive:true},
                {label:'Total Return', value:fmtMoney(lTotRet), bold:true, divider:true, color:'#2d6a4f'},
                {label:'Total Received', value:fmtMoney(lTotRec), bold:true},
                {label:'  \u2212 Total Invested', value:'('+fmtMoney(lTotalInv)+')', red:true},
                {label:'Net Profit', value:fmtMoney(lNetP), bold:true, divider:true, color:lNetP>=0?'#2d6a4f':'#c0392b'},
              ].map((r,i)=><PRow key={i} {...r} />)}
            </div>
          )}

          {/* ── NOTES & COMPS TAB ── */}
          {activeTab==='notes' && (
            <div>
              <SecHeader title="Comparable Sales" />
              {[[c1a,setC1a,c1p,setC1p,c1s,setC1s],[c2a,setC2a,c2p,setC2p,c2s,setC2s],[c3a,setC3a,c3p,setC3p,c3s,setC3s]].map(([addr,sAddr,price2,sPrice,sqft2,sSqft],i)=>(
                <div key={i} style={{marginBottom:'10px',paddingBottom:'10px',borderBottom:i<2?'1px solid #e0e0e0':'none'}}>
                  <div style={{fontSize:'12px',color:'#888',marginBottom:'5px'}}>Comp {i+1}</div>
                  <div style={{display:'grid',gridTemplateColumns:'2fr 1fr 1fr',gap:'6px'}}>
                    <input value={addr} onChange={e=>sAddr(e.target.value)} placeholder="Address" style={{padding:'5px 8px',border:'1px solid #ddd',borderRadius:'4px',fontSize:'12px'}} />
                    <input value={price2} onChange={e=>sPrice(e.target.value)} placeholder="Sale price" style={{padding:'5px 8px',border:'1px solid #ddd',borderRadius:'4px',fontSize:'12px'}} />
                    <input value={sqft2} onChange={e=>sSqft(e.target.value)} placeholder="Sq ft" style={{padding:'5px 8px',border:'1px solid #ddd',borderRadius:'4px',fontSize:'12px'}} />
                  </div>
                </div>
              ))}
              <SecHeader title="Documents & Notes" />
              <div style={{marginBottom:'10px'}}>
                <label style={{fontSize:'12px',color:'#555',display:'block',marginBottom:'3px'}}>Document / Photo Links</label>
                <input value={docLinks} onChange={e=>setDocLinks(e.target.value)} placeholder="Google Drive, Dropbox, or other links"
                  style={{width:'100%',padding:'6px 8px',border:'1px solid #ddd',borderRadius:'4px',fontSize:'13px',boxSizing:'border-box'}} />
              </div>
              <div>
                <label style={{fontSize:'12px',color:'#555',display:'block',marginBottom:'3px'}}>Notes / Observations</label>
                <textarea value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Property condition, neighborhood notes, auction observations..."
                  rows={5} style={{width:'100%',padding:'6px 8px',border:'1px solid #ddd',borderRadius:'4px',fontSize:'13px',resize:'vertical',boxSizing:'border-box'}} />
              </div>
            </div>
          )}

        </div>{/* end tab content */}

        {/* Footer */}
        <div style={{display:'flex',gap:'8px',marginTop:'14px'}}>
          <button onClick={handleSave} style={{flex:1,padding:'10px',backgroundColor:'#1a1a2e',color:'white',border:'none',borderRadius:'6px',cursor:'pointer',fontWeight:'bold',fontSize:'14px'}}>Save Changes</button>
          <button onClick={handleExport} style={{padding:'10px 14px',backgroundColor:'#2d6a4f',color:'white',border:'none',borderRadius:'6px',cursor:'pointer',fontWeight:'bold',fontSize:'14px',whiteSpace:'nowrap'}}>Export CSV</button>
          <button onClick={onClose} style={{padding:'10px 16px',backgroundColor:'#f0f0f0',color:'#555',border:'none',borderRadius:'6px',cursor:'pointer'}}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
function DueDiligence({ deals, onUpdateDeal, onDeleteDeal }) {
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [filterType,   setFilterType]   = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');

  const types    = ['All','Tax Deed','Tax Lien','Fix & Flip','Multi-Family'];
  const statuses = ['All'].concat(STATUS_PIPELINE);

  const filtered = deals.filter(d =>
    (filterType==='All'   || d.type===filterType) &&
    (filterStatus==='All' || d.status===filterStatus)
  );

  function handleExport() {
    if (!filtered.length) return;
    downloadCSV('byg-due-diligence-'+new Date().toISOString().slice(0,10)+'.csv', buildDealsCSV(filtered));
  }

  if (deals.length===0) return (
    <div>
      <h2 style={{marginBottom:'4px',fontSize:'20px'}}>Due Diligence</h2>
      <p style={{fontSize:'13px',color:'#888',marginBottom:'16px'}}>Track every deal from research to close</p>
      <div style={{backgroundColor:'white',borderRadius:'10px',padding:'40px 20px',textAlign:'center',boxShadow:'0 1px 3px rgba(0,0,0,0.08)'}}>
        <div style={{fontSize:'40px',marginBottom:'12px'}}>📋</div>
        <div style={{fontSize:'16px',fontWeight:'bold',color:'#333',marginBottom:'6px'}}>No deals yet</div>
        <div style={{fontSize:'13px',color:'#888'}}>Go to the Deal Calculator, run your numbers, then hit<br/><strong>"Save to Due Diligence"</strong> on the scorecard tab.</div>
      </div>
    </div>
  );

  return (
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:'8px',marginBottom:'16px'}}>
        <div>
          <h2 style={{margin:0,fontSize:'20px'}}>Due Diligence</h2>
          <p style={{fontSize:'13px',color:'#888',margin:'4px 0 0'}}>Track every deal from research to close</p>
        </div>
        <button onClick={handleExport} disabled={!filtered.length}
          style={{padding:'8px 14px',backgroundColor:filtered.length?'#2d6a4f':'#ccc',color:'white',border:'none',borderRadius:'6px',cursor:filtered.length?'pointer':'default',fontSize:'13px',fontWeight:'bold',whiteSpace:'nowrap'}}>
          {'\u2193'} Export to Excel / Sheets ({filtered.length})
        </button>
      </div>

      <Scorecard deals={deals} />

      <div style={{display:'flex',gap:'8px',marginBottom:'12px',flexWrap:'wrap'}}>
        <div style={{display:'flex',gap:'4px',alignItems:'center',flexWrap:'wrap'}}>
          <span style={{fontSize:'12px',color:'#888'}}>Type:</span>
          {types.map(t=>(
            <button key={t} onClick={()=>setFilterType(t)} style={{padding:'4px 10px',borderRadius:'12px',border:'none',cursor:'pointer',fontSize:'12px',backgroundColor:filterType===t?'#1a1a2e':'#f0f0f0',color:filterType===t?'white':'#555'}}>{t}</button>
          ))}
        </div>
        <div style={{display:'flex',gap:'4px',alignItems:'center',flexWrap:'wrap'}}>
          <span style={{fontSize:'12px',color:'#888'}}>Status:</span>
          {statuses.map(s=>(
            <button key={s} onClick={()=>setFilterStatus(s)} style={{padding:'4px 10px',borderRadius:'12px',border:'none',cursor:'pointer',fontSize:'12px',backgroundColor:filterStatus===s?'#1a1a2e':'#f0f0f0',color:filterStatus===s?'white':'#555'}}>{s}</button>
          ))}
        </div>
      </div>

      <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
        {filtered.length===0 ? (
          <div style={{backgroundColor:'white',padding:'20px',borderRadius:'8px',textAlign:'center',color:'#888',fontSize:'13px'}}>No deals match the selected filters.</div>
        ) : filtered.map(deal=>{
          const m=deal.metrics||{}, isMulti=deal.type==='Multi-Family';
          const cardStats=getCardStats(isMulti,m);
          return (
            <div key={deal.id} style={{backgroundColor:'white',borderRadius:'8px',padding:'14px',boxShadow:'0 1px 3px rgba(0,0,0,0.08)',cursor:'pointer'}} onClick={()=>setSelectedDeal(deal)}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:'8px'}}>
                <div style={{display:'flex',gap:'6px',flexWrap:'wrap'}}>
                  <TypeBadge type={deal.type} />
                  <StatusBadge status={deal.status} />
                  {deal.irsLien&&<span style={{backgroundColor:'#ffebee',color:'#c62828',padding:'3px 8px',borderRadius:'12px',fontSize:'11px',fontWeight:'bold'}}>! IRS Lien</span>}
                  {deal.titleIssues&&<span style={{backgroundColor:'#fff8e1',color:'#f57f17',padding:'3px 8px',borderRadius:'12px',fontSize:'11px',fontWeight:'bold'}}>! Title Issues</span>}
                </div>
                <div style={{display:'flex',gap:'8px',alignItems:'center'}}>
                  <span style={{fontSize:'11px',color:'#aaa'}}>{new Date(deal.savedAt).toLocaleDateString()}</span>
                  <button onClick={e=>{e.stopPropagation();if(window.confirm('Delete this deal?'))onDeleteDeal(deal.id);}}
                    style={{background:'none',border:'none',color:'#ccc',cursor:'pointer',fontSize:'16px',padding:'0 4px'}}>[x]</button>
                </div>
              </div>
              {deal.address&&<div style={{fontSize:'14px',fontWeight:'bold',color:'#1a1a2e',marginBottom:'6px'}}>{deal.address}</div>}
              {deal.apn&&<div style={{fontSize:'12px',color:'#888',marginBottom:'6px'}}>APN: {deal.apn}</div>}
              <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'6px'}}>
                {cardStats.map((stat,i)=>(
                  <div key={i} style={{backgroundColor:'#f8f8f8',padding:'6px 8px',borderRadius:'6px'}}>
                    <div style={{fontSize:'10px',color:'#888'}}>{stat.label}</div>
                    <div style={{fontSize:'13px',fontWeight:'bold',color:'#333'}}>{stat.value}</div>
                  </div>
                ))}
              </div>
              {deal.notes&&<div style={{marginTop:'8px',fontSize:'12px',color:'#666',fontStyle:'italic',borderTop:'1px solid #f0f0f0',paddingTop:'6px'}}>{deal.notes.slice(0,100)}{deal.notes.length>100?'...':''}</div>}
            </div>
          );
        })}
      </div>

      {selectedDeal&&(
        <DealDetail
          deal={selectedDeal}
          onUpdate={(id,updates)=>{ onUpdateDeal(id,updates); setSelectedDeal(prev=>({...prev,...updates})); }}
          onClose={()=>setSelectedDeal(null)}
        />
      )}
    </div>
  );
}

export default DueDiligence;
